import stepsJson from "../../data/battle-procedure-steps.json";
import {
  isRecord,
  validationFailure,
  validationSuccess,
  type ValidationResult,
} from "../../data/validation";

/**
 * The sixteen battle-procedure steps, B-GL-305-008 paragraph 0119.
 *
 * The enumeration is held whole. A second enumeration in the 207.01 teaching
 * deck places the supplementary warning order at step 8; that variant is
 * deliberately not encoded and the two are not reconciled.
 *
 * `inRun` marks the authorised slice rather than deleting steps, so the
 * doctrinal sequence stays intact and the run scope is a data change.
 */
export type BattleProcedurePhase =
  | "Consolidate"
  | "Execute"
  | "Plan"
  | "Prepare";

export type BattleProcedureCapture =
  | "notes"
  | "orders-playback"
  | "trainee-output";

export interface BattleProcedureStep {
  readonly capture: BattleProcedureCapture;
  /** True on the step that issues the trace. Doc 03 section 7.3. */
  readonly issuesTrace?: boolean;
  readonly confirmationPrompt?: string;
  readonly countdownMinutes?: number;
  readonly id: string;
  readonly inRun: boolean;
  readonly number: number;
  readonly phase: BattleProcedurePhase;
  readonly requiresConfirmation?: boolean;
  readonly title: string;
}

export interface BattleProcedureRunScope {
  readonly end: string;
  readonly note: string;
  readonly source: string;
  readonly start: string;
}

export interface BattleProcedureTraceGate {
  readonly lockMinutes: number;
  readonly note: string;
  readonly quote: string;
  readonly source: string;
}

export interface BattleProcedureSequence {
  readonly note: string;
  readonly traceGate: BattleProcedureTraceGate;
  readonly runScope: BattleProcedureRunScope;
  readonly schemaVersion: 1;
  readonly source: string;
  readonly steps: readonly BattleProcedureStep[];
}

const PHASES: readonly string[] = ["Consolidate", "Execute", "Plan", "Prepare"];
const CAPTURES: readonly string[] = [
  "notes",
  "orders-playback",
  "trainee-output",
];

function nonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim() !== "";
}

function validateStep(
  value: unknown,
  index: number,
  source: string,
): ValidationResult<BattleProcedureStep> {
  const path = `$.steps[${String(index)}]`;
  if (!isRecord(value)) {
    return validationFailure(source, path, "expected a JSON object");
  }
  if (value.number !== index + 1) {
    return validationFailure(
      source,
      `${path}.number`,
      `expected the doctrinal step number ${String(index + 1)}`,
    );
  }
  for (const key of ["id", "title"] as const) {
    if (!nonEmptyString(value[key])) {
      return validationFailure(
        source,
        `${path}.${key}`,
        "expected a non-empty string",
      );
    }
  }
  if (typeof value.phase !== "string" || !PHASES.includes(value.phase)) {
    return validationFailure(
      source,
      `${path}.phase`,
      "expected Plan, Prepare, Execute or Consolidate",
    );
  }
  if (typeof value.capture !== "string" || !CAPTURES.includes(value.capture)) {
    return validationFailure(
      source,
      `${path}.capture`,
      "expected notes, orders-playback or trainee-output",
    );
  }
  if (typeof value.inRun !== "boolean") {
    return validationFailure(source, `${path}.inRun`, "expected a boolean");
  }
  if (
    value.issuesTrace !== undefined &&
    typeof value.issuesTrace !== "boolean"
  ) {
    return validationFailure(
      source,
      `${path}.issuesTrace`,
      "expected a boolean",
    );
  }
  if (
    value.countdownMinutes !== undefined &&
    (typeof value.countdownMinutes !== "number" ||
      !Number.isFinite(value.countdownMinutes) ||
      value.countdownMinutes <= 0)
  ) {
    return validationFailure(
      source,
      `${path}.countdownMinutes`,
      "expected a positive number of minutes",
    );
  }
  if (
    value.requiresConfirmation === true &&
    !nonEmptyString(value.confirmationPrompt)
  ) {
    return validationFailure(
      source,
      `${path}.confirmationPrompt`,
      "a step that requires confirmation must state what is being confirmed",
    );
  }
  return validationSuccess(value as unknown as BattleProcedureStep);
}

export function validateBattleProcedureSequence(
  value: unknown,
  source: string,
): ValidationResult<BattleProcedureSequence> {
  if (!isRecord(value)) {
    return validationFailure(source, "$", "expected a JSON object");
  }
  if (value.schemaVersion !== 1) {
    return validationFailure(
      source,
      "$.schemaVersion",
      "expected schemaVersion 1",
    );
  }
  for (const key of ["source", "note"] as const) {
    if (!nonEmptyString(value[key])) {
      return validationFailure(
        source,
        `$.${key}`,
        "expected a non-empty string",
      );
    }
  }
  if (!isRecord(value.runScope)) {
    return validationFailure(source, "$.runScope", "expected a JSON object");
  }
  for (const key of ["source", "start", "end", "note"] as const) {
    if (!nonEmptyString(value.runScope[key])) {
      return validationFailure(
        source,
        `$.runScope.${key}`,
        "expected a non-empty string",
      );
    }
  }
  if (!isRecord(value.traceGate)) {
    return validationFailure(source, "$.traceGate", "expected a JSON object");
  }
  if (
    typeof value.traceGate.lockMinutes !== "number" ||
    !Number.isFinite(value.traceGate.lockMinutes) ||
    value.traceGate.lockMinutes <= 0
  ) {
    return validationFailure(
      source,
      "$.traceGate.lockMinutes",
      "expected a positive number of minutes",
    );
  }
  for (const key of ["source", "quote", "note"] as const) {
    if (!nonEmptyString(value.traceGate[key])) {
      return validationFailure(
        source,
        `$.traceGate.${key}`,
        "expected a non-empty string",
      );
    }
  }
  if (!Array.isArray(value.steps) || value.steps.length !== 16) {
    return validationFailure(
      source,
      "$.steps",
      "expected the sixteen doctrinal steps",
    );
  }
  for (const [index, step] of value.steps.entries()) {
    const result = validateStep(step, index, source);
    if (!result.ok) return result;
  }
  return validationSuccess(value as unknown as BattleProcedureSequence);
}

const validation = validateBattleProcedureSequence(
  stepsJson,
  "app/data/battle-procedure-steps.json",
);

/* v8 ignore next -- the imported, committed JSON is validated in unit tests */
if (!validation.ok) throw new Error(validation.error.message);

export const battleProcedureSequence: BattleProcedureSequence = Object.freeze(
  validation.value,
);

/** Index of the step that issues the trace, or -1 when none does. */
export const traceIssueStepIndex: number = battleProcedureSequence.steps
  .filter(({ inRun }) => inRun)
  .findIndex(({ issuesTrace }) => issuesTrace === true);

/** The steps the authorised run actually sequences, in doctrinal order. */
export const battleProcedureRunSteps: readonly BattleProcedureStep[] =
  Object.freeze(battleProcedureSequence.steps.filter(({ inRun }) => inRun));
